import * as Utils from '_utils';
import * as Types from '../types';
export const MINIMIZED_WIDGETS_PLACEHOLDER = 'MinimizedWidgets';
export const EMPTY_WIDGET_PLACEMENT_MANAGER = {
    getWidgetById: () => undefined,
    getWidgets: () => [],
    showWidget: () => { },
    minimizeWidget: () => { },
    openFullScreen: () => { },
    close: () => { },
    setPlacement: () => { },
};
export class WidgetPlacementManager {
    widgets;
    setWidgets;
    setOverlay;
    constructor(widgets, setWidgets, setOverlay) {
        this.widgets = widgets;
        this.setWidgets = setWidgets;
        this.setOverlay = setOverlay;
    }
    static loadWidgetPlacements(widgets) {
        const placements = Utils.getLocalData('widgetPlacements');
        if (!placements) {
            return;
        }
        for (const widget of WidgetPlacementManager.iterateWidgets(widgets)) {
            const widgetPlacement = placements[widget.id];
            if (widgetPlacement) {
                widget.placement = widgetPlacement;
            }
        }
    }
    getWidgets(widgets, placementType, placeholderName) {
        const result = [];
        function matchPlaceholderName(placement) {
            return placement.placeholderName === placeholderName;
        }
        function matchesQuery(placement) {
            return placement.type === placementType && matchPlaceholderName(placement);
        }
        function getPlacementForQuery(widget) {
            // Prefer explicit user placement when it matches current placeholder query.
            if (widget.placement) {
                if (matchesQuery(widget.placement)) {
                    return widget.placement;
                }
                // Keep tiles defaults visible when widget was manually moved elsewhere.
                if (widget.defaultPlacement &&
                    widget.defaultPlacement.type === Types.WidgetPlaceholderType.Tiles &&
                    matchesQuery(widget.defaultPlacement)) {
                    return widget.defaultPlacement;
                }
                return undefined;
            }
            if (widget.defaultPlacement && matchesQuery(widget.defaultPlacement)) {
                return widget.defaultPlacement;
            }
            return undefined;
        }
        for (const widget of this.walkWidgets(widgets)) {
            const placement = getPlacementForQuery(widget);
            if (placement) {
                result.push([widget, placement]);
            }
        }
        return result;
    }
    getWidgetById(widgetId) {
        return this.findWidgetById(this.widgets, widgetId);
    }
    showWidget(widgetId) {
        const placement = {
            type: Types.WidgetPlaceholderType.Floating,
            size: Types.WidgetSize.Frame,
            x: 100,
            y: 100,
            width: 300,
            height: 200,
            isActive: true,
        };
        this.setPlacement(widgetId, placement);
    }
    minimizeWidget(widgetId) {
        const dockedPlacement = {
            type: Types.WidgetPlaceholderType.Docked,
            placeholderName: MINIMIZED_WIDGETS_PLACEHOLDER,
            size: Types.WidgetSize.Button,
        };
        this.setPlacement(widgetId, dockedPlacement);
    }
    openFullScreen(widgetId) {
        const widget = this.getWidgetById(widgetId);
        if (!widget)
            return;
        const fullScreenPlacement = {
            type: Types.WidgetPlaceholderType.FullScreen,
            size: Types.WidgetSize.FullScreen,
        };
        this.setPlacement(widgetId, fullScreenPlacement);
    }
    close(widgetId) {
        this.setPlacement(widgetId, undefined);
    }
    setPlacement(widgetId, newPlacement) {
        const widget = this.getWidgetById(widgetId);
        if (!widget) {
            console.warn(`Widget with id ${widgetId} not found`);
            return;
        }
        // TODO: Validate new placement
        const oldIsFullScreen = this.isFullScreenPlacement(widget.placement);
        const newIsFullScreen = this.isFullScreenPlacement(newPlacement);
        widget.placement = newPlacement;
        console.debug(`Widget ${widgetId} placement changed to`, newPlacement);
        this.handleFullScreenPlacementChange(widgetId, oldIsFullScreen, newIsFullScreen);
        this.doUpdate();
    }
    isFullScreenPlacement(placement) {
        return placement?.type === Types.WidgetPlaceholderType.FullScreen;
    }
    removeFullScreenFromOtherWidgets(excludeWidgetId) {
        for (const widget of this.walkWidgets(this.widgets)) {
            if (widget.id !== excludeWidgetId && this.isFullScreenPlacement(widget.placement)) {
                widget.placement = undefined;
            }
        }
    }
    findWidgetById(widgets, widgetId) {
        for (const widget of this.walkWidgets(widgets)) {
            if (widget.id === widgetId) {
                return widget;
            }
        }
        return undefined;
    }
    *walkWidgets(widgets) {
        yield* WidgetPlacementManager.iterateWidgets(widgets);
    }
    static *iterateWidgets(widgets) {
        const stack = [...widgets].reverse();
        const visited = new Set();
        while (stack.length > 0) {
            const widget = stack.pop();
            if (!widget || visited.has(widget)) {
                continue;
            }
            visited.add(widget);
            yield widget;
            if (widget.children && widget.children.length > 0) {
                for (let i = widget.children.length - 1; i >= 0; i -= 1) {
                    stack.push(widget.children[i]);
                }
            }
        }
    }
    static saveWidgetPlacements(widgets) {
        const placements = {};
        for (const widget of widgets) {
            if (widget.placement) {
                placements[widget.id] = widget.placement;
            }
        }
        Utils.setLocalData('widgetPlacements', placements);
    }
    handleFullScreenPlacementChange(widgetId, oldIsFullScreen, newIsFullScreen) {
        if (!this.setOverlay)
            return;
        if (newIsFullScreen) {
            // New placement is fullscreen: remove fullscreen from others and set overlay
            this.removeFullScreenFromOtherWidgets(widgetId);
            this.setOverlay({
                type: Types.OverlayType.WidgetFullScreen,
                widgetId,
            });
        }
        else if (oldIsFullScreen) {
            // Old placement was fullscreen but new placement is not: clear overlay
            this.setOverlay({ type: Types.OverlayType.Null });
        }
    }
    doUpdate() {
        WidgetPlacementManager.saveWidgetPlacements(this.walkWidgets(this.widgets));
        if (this.setWidgets) {
            this.setWidgets([...this.widgets]);
        }
    }
}
